import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useTranslationService, detectUserLanguage } from '../utils/translationService'
import { DEFAULT_LLM, DEFAULT_MODELS } from '@/utils/constants'
import { cacheManager } from '../utils/cacheManager'
import { apiConfig } from '../utils/apiConfig'

const defaultLanguage = 'en'
const defaultLength = 320
const defaultFontSize = 1.1
const defaultAudience = 'adult'

export const useSettingsStore = defineStore('settings', () => {
  // Initialize defaults on first visit. Language auto-detects from
  // navigator.language (#85) — chip in the header lets the user
  // override if the guess is wrong.
  if (!localStorage.getItem('hasVisitedBefore')) {
    localStorage.setItem('language', detectUserLanguage())
    localStorage.setItem('textLength', String(defaultLength))
    localStorage.setItem('forecastFontSize', String(defaultFontSize))
    localStorage.setItem('selectedAudience', defaultAudience)
    localStorage.setItem('modelString', DEFAULT_MODELS)
    localStorage.setItem('selectedLLM', DEFAULT_LLM)
    localStorage.setItem('selectedModels', JSON.stringify({
      'ecmwf-aifs': true, 'ecmwf-ifs': true, 'ncep-gfs': true, 'dwd-icon': true
    }))
    localStorage.setItem('hasVisitedBefore', 'true')
  }

  const location = ref(localStorage.getItem('location') || '')
  const language = ref(localStorage.getItem('language') || defaultLanguage)
  const textLength = ref(Number(localStorage.getItem('textLength')) || defaultLength)
  const forecastFontSize = ref(Number(localStorage.getItem('forecastFontSize')) || defaultFontSize)
  const countryCode = ref(localStorage.getItem('countryCode') || '')
  const selectedAudience = ref(localStorage.getItem('selectedAudience') || defaultAudience)
  const modelString = ref(localStorage.getItem('modelString') || DEFAULT_MODELS)
  const selectedLLM = ref(localStorage.getItem('selectedLLM') || DEFAULT_LLM)
  // Chat-storage opt-out (#53). Default false = conversations stored
  // on the server (for diagnosis + #40's history drawer). Anon users
  // persist this purely locally; signed-in users also sync to the
  // wital_user.chat_storage_opted_out DB column via pushToServer.
  const chatStorageOptedOut = ref(localStorage.getItem('chatStorageOptedOut') === 'true')

  // Persist all state to localStorage + server sync
  let syncTimer: ReturnType<typeof setTimeout> | null = null

  watch([location, language, textLength, forecastFontSize, countryCode, selectedAudience, modelString, selectedLLM, chatStorageOptedOut], () => {
    localStorage.setItem('location', location.value)
    localStorage.setItem('language', language.value)
    localStorage.setItem('textLength', String(textLength.value))
    localStorage.setItem('forecastFontSize', String(forecastFontSize.value))
    localStorage.setItem('countryCode', countryCode.value)
    localStorage.setItem('selectedAudience', selectedAudience.value)
    localStorage.setItem('modelString', modelString.value)
    localStorage.setItem('selectedLLM', selectedLLM.value)
    localStorage.setItem('chatStorageOptedOut', String(chatStorageOptedOut.value))

    // Debounced server sync when logged in
    if (syncTimer) clearTimeout(syncTimer)
    syncTimer = setTimeout(() => pushToServer(), 2000)
  })

  function toServerData(): Record<string, any> {
    return {
      location: location.value,
      language: language.value,
      textLength: textLength.value,
      forecastFontSize: forecastFontSize.value,
      countryCode: countryCode.value,
      selectedAudience: selectedAudience.value,
      modelString: modelString.value,
      selectedLLM: selectedLLM.value,
      chatStorageOptedOut: chatStorageOptedOut.value,
    }
  }

  // Sticky for the session: once a deep-link (URL ?location= or a push
  // notification tap) populates the active location, the subsequent
  // boot-time pullFromServer race must not clobber it (#174 follow-up).
  // Reset only by clearSession (logout); the next cold start starts
  // fresh from the user's saved server-side location.
  const deepLinkLocationActive = ref(false)
  function markDeepLinkLocation() { deepLinkLocationActive.value = true }
  function clearDeepLinkLocation() { deepLinkLocationActive.value = false }

  function applyFromServer(data: Record<string, any>) {
    if (data.location && !deepLinkLocationActive.value) location.value = data.location
    if (data.language) language.value = data.language
    if (data.textLength) textLength.value = Number(data.textLength)
    if (data.forecastFontSize) forecastFontSize.value = Number(data.forecastFontSize)
    if (data.countryCode && !deepLinkLocationActive.value) countryCode.value = data.countryCode
    if (data.selectedAudience) selectedAudience.value = data.selectedAudience
    if (data.modelString) modelString.value = data.modelString
    if (data.selectedLLM) selectedLLM.value = data.selectedLLM
    if (typeof data.chatStorageOptedOut === 'boolean') {
      chatStorageOptedOut.value = data.chatStorageOptedOut
    }
  }

  async function pushToServer() {
    const token = localStorage.getItem('wital_token')
    if (!token) return
    try {
      await fetch(apiConfig.buildUrl('/api/user/preferences'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ data: toServerData() }),
      })
    } catch {
      // Silent — localStorage is the fallback
    }
  }

  async function pullFromServer() {
    const token = localStorage.getItem('wital_token')
    if (!token) return
    try {
      const resp = await fetch(apiConfig.buildUrl('/api/user/preferences'), {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (resp.ok) {
        const { data } = await resp.json()
        if (data && Object.keys(data).length > 0) {
          applyFromServer(data)
        }
      }
    } catch {
      // Silent — use localStorage values
    }
  }

  const setLocation = (newLocation: string, newCountryCode?: string) => {
    if (!newLocation.trim()) return
    location.value = newLocation.trim()
    countryCode.value = newCountryCode?.trim() || ''
    cacheManager.clearAllCache()
    window.dispatchEvent(new CustomEvent('citySelectionStarted', { detail: { city: location.value } }))
  }

  const setLanguage = async (newLanguage: string) => {
    language.value = newLanguage
    cacheManager.clearAllCache()
    const translationService = useTranslationService()
    if (translationService && newLanguage !== 'en') {
      try { await translationService.changeLanguage(newLanguage) } catch {}
    }
  }

  return {
    location, language, textLength, forecastFontSize, countryCode,
    selectedAudience, modelString, selectedLLM, chatStorageOptedOut,
    setLocation, setLanguage,
    pushToServer, pullFromServer,
    markDeepLinkLocation, clearDeepLinkLocation,
  }
})
